import { memo, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';

export interface MobileEntityCardShellProps {
  readonly entityLabel: string;
  /**
   * Legacy: entire body is activating. Prefer {@link activationChildren} +
   * {@link nonActivatingChildren} when nested controls must stay outside activate.
   */
  readonly children?: ReactNode;
  /**
   * Nodes inside the open-detail activate control (card surface or Detail CTA chrome).
   * Preferred over legacy {@link children} when splitting activating vs non-activating body.
   * With {@link detailActivation} `"none"`, rendered as non-clickable body content.
   */
  readonly activationChildren?: ReactNode;
  /**
   * Sibling nodes OUTSIDE the activate control (qty, visibility, nested buttons).
   * Never nested under the activate `<button>`.
   */
  readonly nonActivatingChildren?: ReactNode;
  readonly actions?: ReactNode;
  readonly hasInnerActions?: boolean;
  readonly detailLabel: string;
  /** Required for `button` / `card` activation; unused when `detailActivation="none"`. */
  readonly onOpenDetail?: () => void;
  /**
   * `button` (default): footer Detail CTA.
   * `card`: whole-card activation opens detail; no Detail twin in actions chrome.
   * `none`: shared chrome only — no Detail CTA / no activate control; body is non-clickable.
   * Secondary actions belong in {@link actions} (stopPropagation via EntityListRowActions).
   */
  readonly detailActivation?: 'button' | 'card' | 'none';
  readonly className?: string;
  readonly testId?: string;
}

/**
 * Shared compact card shell.
 * - `detailActivation="button"`: explicit Detail CTA (legacy / no secondary corner actions).
 * - `detailActivation="card"`: card surface opens detail; actions slot is secondary-only.
 * - `detailActivation="none"`: Pattern A chrome without activate / Detail CTA.
 * - Prefer `activationChildren` + `nonActivatingChildren` so nested controls are never
 *   descendants of the activate `<button>`. Legacy `children` alone still activates the body.
 * Body children should follow Spec1-P10 five-slot order via `MobileCardSlot`.
 */
export const MobileEntityCardShell = memo<MobileEntityCardShellProps>(({
  entityLabel,
  children,
  activationChildren,
  nonActivatingChildren,
  actions,
  hasInnerActions = false,
  detailLabel,
  onOpenDetail,
  detailActivation = 'button',
  className,
  testId = 'mobile-entity-card-shell',
}) => {
  const cardClass =
    'relative overflow-hidden rounded-xl border border-[var(--color-rail-card-border)] bg-[var(--color-an-surface)] p-3';
  const useNoneActivation = detailActivation === 'none';
  const useCardActivation = detailActivation === 'card';
  const activateBody = activationChildren ?? children;
  let detailActivationAttr: 'none' | 'card' | 'button';
  if (useNoneActivation) {
    detailActivationAttr = 'none';
  } else if (useCardActivation) {
    detailActivationAttr = 'card';
  } else {
    detailActivationAttr = 'button';
  }

  const bodyContent = ((): ReactNode => {
    if (useNoneActivation) {
      return (
        <div className={`relative z-10 flex min-w-0 flex-col gap-2${actions ? ' pr-10' : ''}`}>
          {activateBody}
          {nonActivatingChildren ? (
            <div data-mobile-card-slot="non-activating">{nonActivatingChildren}</div>
          ) : null}
        </div>
      );
    }
    if (useCardActivation) {
      // Keyboard: native button is focusable (tabIndex=0) and activates on Enter/Space —
      // APG grid roving cannot apply to card lists; this is the activation substitute.
      const activatePad = actions ? ' pr-10' : '';
      return (
        <>
          <button
            type="button"
            tabIndex={0}
            className={`relative z-10 flex min-w-0 w-full cursor-pointer flex-col gap-2 border-0 bg-transparent p-0${activatePad} text-left`}
            aria-label={`${detailLabel} ${entityLabel}`}
            onClick={onOpenDetail}
            data-testid={`${testId}-activate`}
          >
            {activateBody}
          </button>
          {nonActivatingChildren ? (
            <div className={`relative z-10 mt-2 flex min-w-0 flex-col gap-2${activatePad}`} data-mobile-card-slot="non-activating">
              {nonActivatingChildren}
            </div>
          ) : null}
        </>
      );
    }
    const activatePad = actions ? ' pr-10' : '';
    return (
      <>
        <div className={`relative z-10 flex min-w-0 flex-col gap-2${activatePad}`}>
          {activateBody}
          {nonActivatingChildren ? (
            <div data-mobile-card-slot="non-activating">{nonActivatingChildren}</div>
          ) : null}
        </div>
        <div
          className="relative z-10 mt-3 flex items-center justify-end"
          data-mobile-card-slot="actions"
          data-mobile-card-actions="detail"
        >
          <button
            type="button"
            className="inline-flex min-h-[44px] min-w-[44px] items-center gap-1 rounded-lg px-2 text-sm font-medium text-[var(--color-an-primary)] hover:bg-[var(--color-an-primary-soft)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-an-primary)]"
            aria-label={`${detailLabel} ${entityLabel}`}
            onClick={onOpenDetail}
            data-testid={`${testId}-detail`}
            data-explicit-detail={hasInnerActions ? 'true' : 'false'}
          >
            <span>{detailLabel}</span>
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </>
    );
  })();

  return (
    <article
      className={[cardClass, className].filter(Boolean).join(' ')}
      data-testid={testId}
      data-mobile-card-anatomy="pattern-a"
      data-detail-activation={detailActivationAttr}
    >
      {actions ? (
        <div className="absolute right-2 top-2 z-20" data-mobile-card-slot="actions">
          {actions}
        </div>
      ) : null}
      {bodyContent}
    </article>
  );
});

MobileEntityCardShell.displayName = 'MobileEntityCardShell';
